/**
 * YouTube 업로드 커스텀 훅
 *
 * 업로드 mutation, WebSocket 알림, 예약 공개, 재업로드 확인 로직을 캡슐화합니다.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { getPlaylistRetrieveQueryKey } from '@/lib/api/orval/endpoints/playlists/playlists';
import { usePlaylistUploadToYoutube } from '@/lib/api/orval/endpoints/youtube-upload/youtube-upload';
import type { PlaylistDetail } from '@/lib/api/orval/models';
import { YOUTUBE_MIN_SCHEDULE_OFFSET_MS } from '@/lib/constants';
import type { CompletionNotification, WebSocketMessage } from '@/lib/hooks/useWebSocket';

import { useToast } from './useToast';
import { useWebSocket } from './useWebSocket';

interface UseYouTubeUploadOptions {
  playlist: PlaylistDetail;
  channelData?: {
    isYoutubeConnected?: boolean;
    name?: string;
    youtubeChannelTitle?: string;
    youtubeAuthStatus?: string | null;
    youtubeAuthExpiresAt?: string | null;
  } | null;
}

export function useYouTubeUpload({ playlist, channelData }: UseYouTubeUploadOptions) {
  const queryClient = useQueryClient();
  const { success: toastSuccess, error: toastError } = useToast();

  // 예약 업로드 상태
  const [isScheduleMode, setIsScheduleMode] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState<string>('');

  // 재업로드 확인 상태
  const [showReuploadConfirm, setShowReuploadConfirm] = useState(false);
  const [pendingUploadAction, setPendingUploadAction] = useState<{
    privacyStatus: 'public' | 'unlisted' | 'private';
    scheduledAt?: string;
  } | null>(null);

  // YouTube 업로드 mutation
  const uploadMutation = usePlaylistUploadToYoutube({
    mutation: {
      onSuccess: () => {
        toastSuccess('YouTube 업로드가 시작되었습니다. 완료되면 알림을 받습니다.');
      },
      onError: (err: Error) => {
        toastError(err.message || 'YouTube 업로드를 시작할 수 없습니다.');
      },
    },
  });

  // WebSocket: YouTube 업로드 완료 실시간 알림
  const { connect, disconnect, on, off } = useWebSocket({
    reconnectAttempts: 3,
  });

  const handleYouTubeUploadComplete = useCallback(
    (message: WebSocketMessage) => {
      const notification = message as CompletionNotification;
      if (notification.status === 'youtube_uploaded' && notification.youtubeUrl) {
        toastSuccess('YouTube 업로드가 완료되었습니다!');
        queryClient.invalidateQueries({
          queryKey: getPlaylistRetrieveQueryKey(playlist.id),
        });
      }
    },
    [playlist.id, queryClient, toastSuccess]
  );

  // WebSocket 연결 (업로드 중일 때만)
  useEffect(() => {
    if (uploadMutation.isPending) {
      connect(playlist.id, 'playlist');
      on('completion_notification', handleYouTubeUploadComplete);

      return () => {
        off('completion_notification', handleYouTubeUploadComplete);
        disconnect();
      };
    }
  }, [
    uploadMutation.isPending,
    playlist.id,
    connect,
    disconnect,
    on,
    off,
    handleYouTubeUploadComplete,
  ]);

  // 재업로드 여부
  const isReupload = useMemo(() => !!playlist.youtubeVideoId, [playlist.youtubeVideoId]);

  // 업로드 가능 여부
  const canUpload = useMemo(() => {
    const hasChannel = !!playlist.channel && !!channelData;
    const hasYouTubeOAuth = channelData?.isYoutubeConnected === true;
    const hasVideo = !!(playlist as PlaylistDetail & { videoUrl?: string }).videoUrl;
    const isAuthExpired = channelData?.youtubeAuthStatus === 'expired';
    return hasChannel && hasYouTubeOAuth && hasVideo && !isAuthExpired;
  }, [playlist, channelData]);

  // 업로드 불가 사유
  const uploadDisabledReason = useMemo(() => {
    if (!playlist.channel) {
      return '플레이리스트에 채널이 연동되지 않았습니다.';
    }
    if (!channelData?.isYoutubeConnected) {
      return '채널이 YouTube에 연동되지 않았습니다.';
    }
    if (channelData?.youtubeAuthStatus === 'expired') {
      return 'YouTube 인증이 만료되었습니다. 채널 설정에서 재연동해주세요.';
    }
    if (!(playlist as PlaylistDetail & { videoUrl?: string }).videoUrl) {
      return '업로드할 비디오가 없습니다.';
    }
    return null;
  }, [playlist, channelData]);

  // 최소 예약 시간 (현재 + 1시간)
  const minScheduleDateTime = useMemo(() => {
    const minDate = new Date(Date.now() + YOUTUBE_MIN_SCHEDULE_OFFSET_MS);
    return minDate.toISOString().slice(0, 16);
  }, []);

  // 업로드 실행 (내부)
  const executeUpload = useCallback(
    (privacyStatus: 'public' | 'unlisted' | 'private' = 'unlisted', scheduledAt?: string) => {
      uploadMutation.mutate({
        id: playlist.id,
        data: {
          privacyStatus,
          scheduledAt: scheduledAt || null,
        },
      });
    },
    [playlist.id, uploadMutation]
  );

  // 업로드 핸들러 (재업로드 확인 포함)
  const handleUploadToYouTube = useCallback(
    (privacyStatus: 'public' | 'unlisted' | 'private' = 'unlisted', scheduledAt?: string) => {
      if (isReupload) {
        setPendingUploadAction({ privacyStatus, scheduledAt });
        setShowReuploadConfirm(true);
        return;
      }
      executeUpload(privacyStatus, scheduledAt);
    },
    [isReupload, executeUpload]
  );

  // 재업로드 확인 핸들러
  const handleConfirmReupload = useCallback(() => {
    if (pendingUploadAction) {
      executeUpload(pendingUploadAction.privacyStatus, pendingUploadAction.scheduledAt);
    }
    setShowReuploadConfirm(false);
    setPendingUploadAction(null);
  }, [pendingUploadAction, executeUpload]);

  // 재업로드 취소 핸들러
  const handleCancelReupload = useCallback(() => {
    setShowReuploadConfirm(false);
    setPendingUploadAction(null);
  }, []);

  // 예약 업로드 핸들러
  const handleScheduledUpload = useCallback(() => {
    if (!scheduledDateTime) {
      toastError('예약 공개 시간을 선택해주세요.');
      return;
    }

    const scheduledDate = new Date(scheduledDateTime);
    const minDate = new Date(Date.now() + YOUTUBE_MIN_SCHEDULE_OFFSET_MS);

    if (scheduledDate < minDate) {
      toastError('예약 공개 시간은 최소 1시간 후로 설정해주세요.');
      return;
    }

    const isoString = scheduledDate.toISOString();
    handleUploadToYouTube('private', isoString);
    setIsScheduleMode(false);
    setScheduledDateTime('');
  }, [scheduledDateTime, toastError, handleUploadToYouTube]);

  return {
    // 상태
    isScheduleMode,
    setIsScheduleMode,
    scheduledDateTime,
    setScheduledDateTime,
    showReuploadConfirm,
    isUploading: uploadMutation.isPending,

    // 파생값
    isReupload,
    canUpload,
    uploadDisabledReason,
    minScheduleDateTime,

    // 핸들러
    handleUploadToYouTube,
    handleScheduledUpload,
    handleConfirmReupload,
    handleCancelReupload,
  };
}
